// Aba "Avisos de clientes" da tela Catalogo Digital — Onda Catalogo Fase 3.
// Lista os pedidos de "me avisa quando chegar" pendentes (deixados na vitrine
// publica). O admin contata o cliente pelo WhatsApp e marca como avisado.
import { useEffect, useState } from 'react';
import { Check, X, MessageCircle } from 'lucide-react';
import { api } from '../../lib/api';
import { useToast } from '../../components/Toast';
import { useDialog } from '../../components/DialogProvider';

interface Aviso {
  id: number;
  produto_id: number;
  whatsapp: string;
  nome: string | null;
  criado_em: string;
  produto_descricao: string;
  produto_codigo: string | null;
  foto_url: string | null;
  estoque_atual: string;
}

function fmtData(s: string): string {
  const d = new Date(s);
  return (
    d.toLocaleDateString('pt-BR') +
    ' ' +
    d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
  );
}

// 5564999999999 -> (64) 99999-9999
function fmtWhatsapp(w: string): string {
  const d = w.startsWith('55') ? w.slice(2) : w;
  if (d.length === 11) return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
  if (d.length === 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return w;
}

export function CatalogoAvisos({ onAtualizou }: { onAtualizou?: () => void }) {
  const toast = useToast();
  const dialog = useDialog();
  const [itens, setItens] = useState<Aviso[]>([]);
  const [carregando, setCarregando] = useState(true);

  async function carregar() {
    setCarregando(true);
    try {
      setItens(await api.get<Aviso[]>('/api/produtos/catalogo-avisos'));
    } catch (e: any) {
      toast.erro(e.message);
    } finally {
      setCarregando(false);
    }
  }

  useEffect(() => {
    carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function linkWhatsapp(a: Aviso): string {
    const msg =
      `Ola${a.nome ? ' ' + a.nome : ''}! Aqui e da Fio de Cobre. ` +
      `O produto "${a.produto_descricao}" que voce pediu pra ser avisado ja chegou na loja. 🙂`;
    return `https://wa.me/${a.whatsapp}?text=${encodeURIComponent(msg)}`;
  }

  async function marcar(a: Aviso, status: 'avisado' | 'cancelado') {
    if (status === 'cancelado') {
      const ok = await dialog.confirmar(
        `Descartar o pedido de aviso de "${a.produto_descricao}"? O cliente nao sera contatado.`,
        { perigoso: true },
      );
      if (!ok) return;
    }
    try {
      await api.patch(`/api/produtos/catalogo-avisos/${a.id}`, { status });
      toast.sucesso(status === 'avisado' ? 'Marcado como avisado' : 'Pedido descartado');
      await carregar();
      onAtualizou?.();
    } catch (e: any) {
      toast.erro(e.message);
    }
  }

  if (carregando) return <p className="muted">Carregando...</p>;

  if (itens.length === 0) {
    return (
      <div className="muted" style={{ padding: 24, lineHeight: 1.7 }}>
        Nenhum pedido de aviso no momento.
        <br />
        Quando um cliente pedir pra ser avisado de um produto sem estoque na vitrine,
        ele aparece aqui.
      </div>
    );
  }

  return (
    <table className="tabela">
      <thead>
        <tr>
          <th style={{ width: 70 }}>FOTO</th>
          <th style={{ width: 80 }}>COD.</th>
          <th>PRODUTO</th>
          <th>CLIENTE</th>
          <th style={{ width: 150 }}>WHATSAPP</th>
          <th style={{ width: 140 }}>PEDIU EM</th>
          <th style={{ width: 110, textAlign: 'center' }}>ESTOQUE</th>
          <th style={{ width: 230 }}>ACOES</th>
        </tr>
      </thead>
      <tbody>
        {itens.map((a) => {
          const voltou = Number(a.estoque_atual) > 0;
          return (
            <tr key={a.id}>
              <td>
                {a.foto_url ? (
                  <img src={a.foto_url} alt="" className="produto-thumb" loading="lazy" />
                ) : (
                  <div className="produto-thumb-vazio" />
                )}
              </td>
              <td>
                <span className="codigo-curto">{a.produto_codigo ?? '—'}</span>
              </td>
              <td>{a.produto_descricao}</td>
              <td>{a.nome || <span className="muted">—</span>}</td>
              <td>{fmtWhatsapp(a.whatsapp)}</td>
              <td>{fmtData(a.criado_em)}</td>
              <td style={{ textAlign: 'center' }}>
                {voltou ? (
                  <span
                    style={{
                      backgroundColor: '#fde68a',
                      color: '#000',
                      padding: '2px 8px',
                      borderRadius: 4,
                      fontSize: '0.75rem',
                      fontWeight: 700,
                    }}
                  >
                    VOLTOU ({Number(a.estoque_atual)})
                  </span>
                ) : (
                  <span className="muted" style={{ fontSize: '0.78rem' }}>
                    sem estoque
                  </span>
                )}
              </td>
              <td>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  <a
                    href={linkWhatsapp(a)}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Abrir conversa no WhatsApp com a mensagem pronta"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 4,
                      backgroundColor: '#25d366',
                      color: '#fff',
                      padding: '4px 8px',
                      borderRadius: 4,
                      fontSize: '0.78rem',
                      fontWeight: 600,
                      textDecoration: 'none',
                    }}
                  >
                    <MessageCircle size={14} /> WhatsApp
                  </a>
                  <button
                    type="button"
                    onClick={() => marcar(a, 'avisado')}
                    title="Marcar como avisado"
                    className="btn-secundario"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 8px', fontSize: '0.78rem' }}
                  >
                    <Check size={14} /> Avisei
                  </button>
                  <button
                    type="button"
                    onClick={() => marcar(a, 'cancelado')}
                    title="Descartar pedido"
                    style={{ background: 'transparent', border: 'none', color: '#dc2626', cursor: 'pointer', padding: 4 }}
                  >
                    <X size={16} />
                  </button>
                </div>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
